import type { UIEvent, RefObject } from 'react';

import { useState, useRef, useCallback, useEffect } from 'react';

export type UseCarouselScrollProps = {
  itemsCount: number;
  maxVisibleItems: number;
  scrollThresholdRatio?: number;
};

export type UseCarouselScrollReturn = {
  carouselRef: RefObject<HTMLDivElement | null>;
  tilesWrapperRef: RefObject<HTMLUListElement | null>;
  leftHidden: boolean;
  rightHidden: boolean;
  handleScroll: (e: UIEvent) => void;
  scrollLeft: () => void;
  scrollRight: () => void;
  setTileWidth: (width: number) => void;
};

export const useCarouselScroll = ({
  itemsCount,
  maxVisibleItems,
  scrollThresholdRatio = 3,
}: UseCarouselScrollProps): UseCarouselScrollReturn => {
  const [leftHidden, setLeftHidden] = useState(true);
  const [rightHidden, setRightHidden] = useState(false);
  const [tileWidth, setTileWidth] = useState(0);

  const carouselRef = useRef<HTMLDivElement>(null);
  const tilesWrapperRef = useRef<HTMLUListElement>(null);

  const handleScroll = useCallback(
    (e: UIEvent) => {
      const { scrollLeft, offsetWidth, scrollWidth } = e.target as HTMLDivElement;
      const productTileWidthThreshold =
        (tilesWrapperRef.current?.children?.[0].clientWidth ?? scrollThresholdRatio) /
        scrollThresholdRatio;

      setLeftHidden(scrollLeft <= productTileWidthThreshold);
      setRightHidden(scrollLeft + offsetWidth > scrollWidth - productTileWidthThreshold);
    },
    [scrollThresholdRatio]
  );

  const scrollLeft = useCallback(() => {
    if (carouselRef.current) {
      carouselRef.current.scrollLeft -= tileWidth;
    }
  }, [tileWidth]);

  const scrollRight = useCallback(() => {
    if (carouselRef.current) {
      carouselRef.current.scrollLeft += tileWidth;
    }
  }, [tileWidth]);

  useEffect(() => {
    setRightHidden(itemsCount <= maxVisibleItems);
  }, [itemsCount, maxVisibleItems]);

  return {
    carouselRef,
    tilesWrapperRef,
    leftHidden,
    rightHidden,
    handleScroll,
    scrollLeft,
    scrollRight,
    setTileWidth,
  };
};
